/**
 * 146. The EXIF facts the photo room keeps, read BEFORE the canvas re-encode
 * strips them all: when the photo was taken (the camera's wall clock, and its
 * UTC offset if the camera wrote one) and the camera make and model.
 *
 * 149 (FED-54, David 10 Oct): AND WHERE IT WAS TAKEN — the GPS latitude and
 * longitude, nothing else from the GPS block (no altitude, no time, no
 * direction). 146 refused to read GPS at all; David reversed that so a photo
 * can be tied to a project. The coordinates go to set_photo_location for
 * photo room admins only, never into the feed copy, and only when the poster
 * leaves "send location and camera details" on (Composer decides).
 *
 * A small hand parser rather than a library: four tags, one segment, and a
 * PWA that has to load on one bar of signal (icons.tsx's argument). Anything
 * unexpected returns nulls — this is metadata, never a reason to fail a post.
 */
export type ExifFacts = {
  /** "2026-10-01T07:12:00" — the camera's wall clock, zone unknown. */
  takenAtLocal: string | null
  /** "-07:00" when the camera recorded it (EXIF OffsetTimeOriginal). */
  takenOffset: string | null
  make: string | null
  model: string | null
  /** 149. Decimal degrees, south and west negative. Null unless both are present and sane. */
  latitude: number | null
  longitude: number | null
}

const NONE: ExifFacts = { takenAtLocal: null, takenOffset: null, make: null, model: null, latitude: null, longitude: null }

/** The first 256 KB is where every camera puts APP1. */
export async function readExif(file: Blob): Promise<ExifFacts> {
  try {
    if (file.type && file.type !== 'image/jpeg') return NONE
    return parseExif(await file.slice(0, 262144).arrayBuffer())
  } catch {
    return NONE
  }
}

export function parseExif(buf: ArrayBuffer): ExifFacts {
  try {
    const v = new DataView(buf)
    if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return NONE
    let at = 2
    while (at + 4 <= v.byteLength) {
      if (v.getUint8(at) !== 0xff) return NONE
      const marker = v.getUint8(at + 1)
      const len = v.getUint16(at + 2)
      if (marker === 0xda || marker === 0xd9) return NONE // image data: no APP1 before it
      if (marker === 0xe1 && at + 10 <= v.byteLength &&
          v.getUint32(at + 4) === 0x45786966 && v.getUint16(at + 8) === 0x0000) {
        return readTiff(v, at + 10, Math.min(v.byteLength, at + 2 + len))
      }
      at += 2 + len
    }
    return NONE
  } catch {
    return NONE
  }
}

function readTiff(v: DataView, tiff: number, end: number): ExifFacts {
  const order = v.getUint16(tiff)
  if (order !== 0x4949 && order !== 0x4d4d) return NONE
  const le = order === 0x4949
  const u16 = (o: number) => v.getUint16(o, le)
  const u32 = (o: number) => v.getUint32(o, le)
  if (u16(tiff + 2) !== 42) return NONE

  const ascii = (entry: number): string | null => {
    const type = u16(entry + 2)
    const count = u32(entry + 4)
    if (type !== 2 || count === 0 || count > 256) return null
    const start = count <= 4 ? entry + 8 : tiff + u32(entry + 8)
    if (start + count > end) return null
    let s = ''
    for (let i = 0; i < count; i++) {
      const c = v.getUint8(start + i)
      if (c === 0) break
      s += String.fromCharCode(c)
    }
    s = s.trim()
    return s || null
  }

  const ifd = (offset: number, want: Record<number, (entry: number) => void>) => {
    const base = tiff + offset
    if (base + 2 > end) return
    const n = u16(base)
    for (let i = 0; i < n && i < 512; i++) {
      const entry = base + 2 + i * 12
      if (entry + 12 > end) return
      const fn = want[u16(entry)]
      if (fn) fn(entry)
    }
  }

  let make: string | null = null
  let model: string | null = null
  let exifIfd = 0
  let gpsIfd = 0
  ifd(u32(tiff + 4), {
    0x010f: (e) => (make = ascii(e)),
    0x0110: (e) => (model = ascii(e)),
    0x8769: (e) => (exifIfd = u32(e + 8)),
    0x8825: (e) => (gpsIfd = u32(e + 8)),
  })

  /* 149. Three RATIONALs (degrees, minutes, seconds) plus an 'N'/'S' or
     'E'/'W' reference. Anything malformed is null, never a guess. */
  const dms = (entry: number): number | null => {
    if (u16(entry + 2) !== 5 || u32(entry + 4) !== 3) return null
    const at = tiff + u32(entry + 8)
    if (at + 24 > end) return null
    const part = (k: number) => {
      const den = u32(at + k * 8 + 4)
      return den === 0 ? NaN : u32(at + k * 8) / den
    }
    const v = part(0) + part(1) / 60 + part(2) / 3600
    return Number.isFinite(v) ? v : null
  }
  let latRef: string | null = null
  let lngRef: string | null = null
  let lat: number | null = null
  let lng: number | null = null
  if (gpsIfd > 0) {
    ifd(gpsIfd, {
      0x0001: (e) => (latRef = ascii(e)),
      0x0002: (e) => (lat = dms(e)),
      0x0003: (e) => (lngRef = ascii(e)),
      0x0004: (e) => (lng = dms(e)),
    })
  }

  let taken: string | null = null
  let offset: string | null = null
  if (exifIfd > 0) {
    ifd(exifIfd, {
      0x9003: (e) => (taken = ascii(e)),
      0x9011: (e) => (offset = ascii(e)),
    })
  }

  return {
    takenAtLocal: exifDate(taken),
    takenOffset: offset && /^[+-]\d{2}:\d{2}$/.test(offset) ? offset : null,
    make: clean(make),
    model: clean(model),
    ...gps(lat, latRef, lng, lngRef),
  }
}

/** Signed decimal degrees, or both null. 0,0 is a camera with no fix, not a place. */
export function gps(
  lat: number | null, latRef: string | null, lng: number | null, lngRef: string | null,
): { latitude: number | null; longitude: number | null } {
  if (lat === null || lng === null || !latRef || !lngRef) return { latitude: null, longitude: null }
  const la = (latRef.toUpperCase().startsWith('S') ? -1 : 1) * lat
  const lo = (lngRef.toUpperCase().startsWith('W') ? -1 : 1) * lng
  if (Math.abs(la) > 90 || Math.abs(lo) > 180 || (la === 0 && lo === 0)) return { latitude: null, longitude: null }
  return { latitude: Math.round(la * 1e6) / 1e6, longitude: Math.round(lo * 1e6) / 1e6 }
}

/** "2026:10:01 07:12:00" → "2026-10-01T07:12:00", or null if it is not a real date. */
export function exifDate(s: string | null): string | null {
  const m = s ? /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(s) : null
  if (!m) return null
  const [y, mo, d, h, mi, se] = m.slice(1).map(Number)
  if (y < 1990 || y > 2100 || mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || se > 59) return null
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`
}

function clean(s: string | null): string | null {
  if (!s) return null
  const t = s.replace(/[^\x20-\x7e]/g, '').replace(/\s+/g, ' ').trim()
  return t ? t.slice(0, 80) : null
}
